import BaseService from './BaseService';
import FileService from './FileService';

export function apiErrorMessage(error, fallback) {
  const data = error && error.response && error.response.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && data.mensagem) return data.mensagem;
  if (data && data.mensagemDetalhada) return data.mensagemDetalhada;
  if (data && data.message) return data.message;
  if (data && data.error) return data.error;
  return fallback;
}

export function uploadKmzMatriz(nsirId, file, onUploadProgress) {
  const formData = new FormData();
  formData.append('arquivo', file);
  return BaseService.postMultipart({
    url: `nsirs/${nsirId}/kmz/matriz`,
    data: formData,
    onUploadProgress,
  });
}

export function fetchKmzClassificacaoRota(nsirId) {
  return BaseService.get({
    url: `nsirs/${nsirId}/kmz/classificacao-rota`,
  });
}

export function fetchKmzSugestao(nsirId, nsirKmzId, params) {
  const search = new URLSearchParams();
  search.set('nsirKmzId', nsirKmzId);
  if (params) {
    const v2 = params.estrategia === 'MTBT_V2';
    if (params.estrategia) search.set('estrategia', params.estrategia);
    if (params.classificacaoRota) search.set('classificacaoRota', params.classificacaoRota);
    if (v2) {
      search.set('pararEmUrbano', params.pararEmUrbano || '');
      search.set('pararEmRural', params.pararEmRural || '');
    }
    if (params.atravessarDispositivos) search.set('atravessarDispositivos', params.atravessarDispositivos);
    if (params.dispositivosFim) search.set('dispositivosFim', params.dispositivosFim);
    if (params.atravessarCategorias) search.set('atravessarCategorias', params.atravessarCategorias);
    if (typeof params.usarRota === 'boolean') search.set('usarRota', params.usarRota ? 'true' : 'false');
  }
  return BaseService.get({
    url: `nsirs/${nsirId}/kmz/matriz/sugestao?${search.toString()}`,
  });
}

export function nomeAnexoRota(headers, fallback = 'rota.kml') {
  const bruto = headers && (headers['content-disposition'] || headers['Content-Disposition']);
  if (!bruto) return fallback;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(bruto);
  if (utf) {
    try {
      return decodeURIComponent(utf[1]);
    } catch (err) {
      return utf[1];
    }
  }
  const simples = /filename="?([^";]+)"?/i.exec(bruto);
  return simples && simples[1] ? simples[1] : fallback;
}

export function arquivoDaRespostaRota(response, fallbackName = 'rota.kml') {
  const blob = response && response.data;
  if (!blob || typeof blob.size !== 'number' || blob.size <= 0) return null;
  const tipo = String(blob.type || '').toLowerCase();
  if (tipo.includes('json') || tipo.includes('html')) return null;
  const file = new File(
    [blob],
    nomeAnexoRota(response.headers, fallbackName),
    { type: blob.type || 'application/octet-stream' },
  );
  file.origemServidor = true;
  return file;
}

export function urlRotaDaLista(data) {
  const lista = Array.isArray(data && data.arquivos) ? data.arquivos : [];
  const entry = lista.find((item) => (
    item
    && String(item.tipo || '').trim().toUpperCase() === 'ROTA'
    && item.url
  ));
  return entry ? String(entry.url) : '';
}

export function nomeArquivoRota(url) {
  const semQuery = String(url || '').split('?')[0];
  let base = '';
  try {
    base = decodeURIComponent(semQuery.split('/').pop() || '');
  } catch (err) {
    base = semQuery.split('/').pop() || '';
  }
  if (base && /\.[a-z0-9]+$/i.test(base)) return base;
  return 'rota.kmz';
}

export async function fetchKmzRota(nsirId) {
  let response;
  try {
    response = await listarKmzNsir(nsirId, 'ROTA');
  } catch (err) {
    const status = err && err.response && err.response.status;
    if (status === 404) return null;
    throw err;
  }
  const url = urlRotaDaLista(response && response.data);
  if (!url) return null;
  const baixado = await FileService.getFile(url);
  return arquivoDaRespostaRota(baixado, nomeArquivoRota(url));
}

export function uploadKmzRota(nsirId, nsirKmzId, file, onUploadProgress) {
  const formData = new FormData();
  formData.append('arquivo', file);
  return BaseService.postMultipart({
    url: `nsirs/${nsirId}/kmz/matriz/${nsirKmzId}/rota`,
    data: formData,
    onUploadProgress,
  });
}

export function calcularKmKmz(nsirId, body) {
  return BaseService.post({
    url: `nsirs/${nsirId}/kmz/matriz/calcular-km`,
    data: body,
  });
}

export function confirmarKmzMatriz(nsirId, body) {
  return BaseService.post({
    url: `nsirs/${nsirId}/kmz/matriz/confirmar`,
    data: body,
  });
}

export function validateKmzServicos(nsirId) {
  return BaseService.get({
    url: `nsirs/${nsirId}/kmz/servicos/validate`,
  });
}

export function validateKmzMatriz(nsirId, nsirKmzId) {
  return BaseService.get({
    url: `nsirs/${nsirId}/kmz/matriz/${nsirKmzId}/validate`,
  });
}

export function confirmarEquipamentoInicialKmz(nsirId, nsirKmzId, nome) {
  return BaseService.put({
    url: `nsirs/${nsirId}/kmz/matriz/${nsirKmzId}/equipamento-inicial`,
    data: { nome },
  });
}

export function listarKmzNsir(nsirId, tipo) {
  const busca = tipo ? `?tipo=${encodeURIComponent(tipo)}` : '';
  return BaseService.get({
    url: `nsirs/${nsirId}/kmz${busca}`,
  });
}

const TIPOS_LISTA_KMZ = ['CRU', 'INSPECAO', 'COMPLETO'];

export async function listarKmzVisiveis(nsirId) {
  const respostas = await Promise.all(
    [null, ...TIPOS_LISTA_KMZ].map((tipo) =>
      listarKmzNsir(nsirId, tipo)
        .then((response) => response && response.data)
        .catch(() => null)
    )
  );
  const arquivos = [];
  respostas.forEach((data) => {
    const lista = Array.isArray(data && data.arquivos) ? data.arquivos : [];
    lista.forEach((entry) => {
      if (!entry || !entry.url || !entry.tipo) return;
      const tipo = String(entry.tipo).trim().toUpperCase();
      if (arquivos.some((item) => String(item.tipo).toUpperCase() === tipo)) return;
      arquivos.push({ ...entry, tipo });
    });
  });
  return { arquivos, consultas: respostas.filter(Boolean) };
}

const NSIR_KMZ_ID_LEGADO = 'legado';

function idSessaoKmz(data) {
  if (!data || data.nsirKmzId == null) return null;
  const id = String(data.nsirKmzId).trim();
  if (!id || id.toLowerCase() === NSIR_KMZ_ID_LEGADO) return null;
  return id;
}

function idComRecorteConfirmado(data) {
  const id = idSessaoKmz(data);
  if (!id) return null;
  const lista = Array.isArray(data.arquivos) ? data.arquivos : [];
  const temInspecao = lista.some(
    (entry) => entry && entry.url && String(entry.tipo || '').trim().toUpperCase() === 'INSPECAO'
  );
  return temInspecao ? id : null;
}

function jaTemSessaoKmz(error) {
  const msg = apiErrorMessage(error, '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return msg.includes('sessao de geracao') || (msg.includes('sessao') && msg.includes('completo'));
}

export function gerarKmzCompletoMatriz(nsirId, nsirKmzId) {
  return BaseService.post({
    url: `nsirs/${nsirId}/kmz/matriz/${nsirKmzId}/completo`,
    data: {},
  });
}

export function gerarKmzCompletoLegado(nsirId) {
  return BaseService.post({
    url: `nsirs/${nsirId}/kmz/legado/completo`,
    data: {},
  });
}

export async function gerarKmzCompletoDaNsir(nsirId) {
  const consulta = await listarKmzNsir(nsirId, 'INSPECAO');
  const nsirKmzId = idComRecorteConfirmado(consulta && consulta.data);
  if (nsirKmzId) return gerarKmzCompletoMatriz(nsirId, nsirKmzId);
  try {
    return await gerarKmzCompletoLegado(nsirId);
  } catch (error) {
    if (!jaTemSessaoKmz(error)) throw error;
    const deNovo = await listarKmzNsir(nsirId, 'INSPECAO');
    const id = idSessaoKmz(deNovo && deNovo.data);
    if (!id) throw error;
    return gerarKmzCompletoMatriz(nsirId, id);
  }
}

export function excluirKmzNsir(nsirId) {
  return BaseService.delete({
    url: `nsirs/${nsirId}/kmz`,
  });
}
